import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import Layout from "@/components/Layout";
import ContactForm from "@/components/ContactForm";
import contact from "@/assets/contact-us.jpg";

const Contact = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section
        className="relative py-24 md:py-32 bg-gradient-subtle overflow-hidden"
        id="contact"
      >
        <div className="absolute inset-0 bg-blue-400/20 bg-blue-200/20 to-background/80"></div>
        <div
          className="absolute inset-0 bg-contain bg-repeat opacity-30"
          style={{ backgroundImage: `url(${contact})` }}
        ></div>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <h1 className="text-4xl lg:text-6xl font-bold text-foreground">
              Get in <span className="text-primary">Touch</span>
            </h1>
            <p className="text-lg lg:text-xl text-muted-foreground leading-relaxed">
              Feel free to drop us a message and we will get back to you at the
              earliest opportunity.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-foreground">
                  Contact Information
                </h2>
                <p className="text-muted-foreground">
                  Get in touch with our team to discuss your training
                  requirements and schedule a consultation.
                </p>
              </div>

              <div className="space-y-6">
                <Card className="shadow-subtle">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-6 h-6 text-white" />
                      </div>
                      <div className="space-y-1">
                        <h3 className="font-semibold text-foreground">
                          Our Location
                        </h3>
                        <div className="text-muted-foreground">
                          <p>TCLA-X Limited</p>
                          <p>41 Forest Close, Off Kenyatta Road</p>
                          <p>P.O. Box 10385-00400</p>
                          <p>Tom Mboya Street, Nairobi, Kenya</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-subtle">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <Phone className="w-6 h-6 text-white" />
                      </div>
                      <div className="space-y-1">
                        <h3 className="font-semibold text-foreground">Phone</h3>
                        <p className="text-muted-foreground">+254780319499</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-subtle">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <Mail className="w-6 h-6 text-white" />
                      </div>
                      <div className="space-y-2">
                        <h3 className="font-semibold text-foreground">Email</h3>
                        <div className="space-y-1 text-muted-foreground">
                          <p>General Inquiries: info@tcla-x.com</p>
                          <p>Director: director@tcla-x.com</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="shadow-subtle">
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-gradient-primary rounded-full flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-white" />
                      </div>
                      <div className="space-y-1">
                        <h3 className="font-semibold text-foreground">
                          Business Hours
                        </h3>
                        <div className="text-muted-foreground">
                          <p>Monday - Friday: 8:00 AM - 5:00 PM</p>
                          <p>Saturday & Sunday: Closed</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Contact Form */}
            <div className="space-y-8">
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-foreground">
                  Send us a Message
                </h2>
                <p className="text-muted-foreground">
                  Fill out the form below and we'll get back to you within 24
                  hours.
                </p>
              </div>

              <Card className="shadow-subtle">
                <CardContent className="p-8">
                  <ContactForm />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
      {/* Map Section */}
      <section className="py-16 bg-gradient-secondary">
        <div className="container mx-auto px-4">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-3xl font-bold text-foreground">Find Us</h2>
            <p className="text-muted-foreground">
              Visit our offices in Kenyatta Road, off Ruiru-Juja Road, Kenya for in-person consultations.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Card className="shadow-subtle overflow-hidden">
              {/* Google Maps Embed */}
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m16!1m12!1m3!1d4105.954631892008!2d36.99271828687533!3d-1.1124197326332188!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!2m1!1s41%20Forest%20Close%2C%20Off%20Kenyatta%20Road%20Kays%20Estate!5e0!3m2!1sen!2ske!4v1756185596823!5m2!1sen!2ske"
                className="w-full h-64 sm:h-72 md:h-96 lg:h-[500px]"
                style={{ border: 2 }}
                allowFullScreen={false}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </Card>
            {/* Directions Button */}
            <div className="text-center">
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=41+Forest+Close,+Off+Kenyatta+Road,+Kays+Estate,+Nairobi"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-primary text-white px-6 py-3 rounded-lg shadow hover:bg-primary/90 transition"
              >
                Get Directions
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
